import type { TFunction } from 'i18next'

import type { DeliveryAddressRoot, FulfillmentOrderRoot } from '@repo/sdk'

import { getFulfillmentOrderPaidAmount } from '../../lib/orderShippingAmounts'
import { formatFoContext } from './orderDisplay'

export interface BuyerGroupSummary {
  /** Number of fulfillment orders shipped together for this buyer. */
  fulfillmentOrderCount: number
  /** Orders in the parcel as the server counts them (#78); null on an older server. */
  orderCount: number | null
  /** The parcel's amount as the server sums it (#78); null on an older server. */
  totalAmount: number | null
  /** The address shared by the whole group, or null when it is missing/ambiguous. */
  commonAddress: DeliveryAddressRoot | null
  /** True when the group spans more than one address (the server refuses to ship then). */
  hasAddressMismatch: boolean
}

/**
 * Aggregates a buyer's new-order group.
 *
 * The detail screen used to render `groupOrders[0]` only, so its counts, amount and address
 * described the first fulfillment order while the list summed the whole group
 * (목록 60,500원 vs 상세 27,000원).
 */
export function summarizeBuyerGroup(
  fulfillmentOrders: readonly FulfillmentOrderRoot[],
): BuyerGroupSummary {
  const addresses = fulfillmentOrders.flatMap((fulfillmentOrder) =>
    fulfillmentOrder.shippingAddress ? [fulfillmentOrder.shippingAddress] : [],
  )
  const addressIds = new Set(addresses.map((address) => address.addressId))

  // Counts and money are the server's (서버 주도 UI 정책 §1): every member carries the same parcel
  // summary, so the first one describes the whole group.
  const parcelSummary = fulfillmentOrders[0]?.parcelSummary
  return {
    fulfillmentOrderCount: fulfillmentOrders.length,
    orderCount: parcelSummary?.orderCount ?? null,
    totalAmount: parcelSummary?.totalAmount.amount ?? null,
    commonAddress: addressIds.size === 1 ? (addresses[0] ?? null) : null,
    hasAddressMismatch: addressIds.size > 1,
  }
}

/** Everything paid for a single fulfillment order: products plus its shipping fee. */
export function sumFulfillmentOrderAmount(fulfillmentOrder: FulfillmentOrderRoot): number {
  return getFulfillmentOrderPaidAmount(fulfillmentOrder)
}

/**
 * Labels of the broadcasts that still block shipping.
 *
 * The warning banner used to look at the first fulfillment order only while the ship button
 * was disabled based on the whole group, so the button could be disabled with no visible reason.
 */
export function findUnendedBroadcastLabels(
  fulfillmentOrders: readonly FulfillmentOrderRoot[],
  t: TFunction,
): string[] {
  return fulfillmentOrders
    .filter(
      (fulfillmentOrder) =>
        fulfillmentOrder.broadcast != null &&
        fulfillmentOrder.broadcast.broadcastStatus !== 'ENDED',
    )
    .map(
      (fulfillmentOrder) =>
        fulfillmentOrder.broadcast?.title || formatFoContext(fulfillmentOrder, t),
    )
}
